import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../utils/apiConfig';
import { Section } from './PosBits';
import { PosTable } from './PosTable';
import { monthLabel, usePaged } from './posUtils';

const STATUS = {
  completed: 'bg-emerald-100 text-emerald-800', completed_with_errors: 'bg-amber-100 text-amber-800',
  replaced: 'bg-slate-200 text-slate-600', failed: 'bg-red-100 text-red-700',
};

const downloadErrors = async (row) => {
  try {
    const r = await axios.get(`${API}/pos/imports/${row.id}/errors`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = `pos_import_errors_${row.filename.replace(/\.xlsx?$/i, '')}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error('Download failed'); }
};

export const ImportHistoryTab = ({ isAdmin, openImport, refreshKey }) => {
  const t = usePaged('imports', { refreshKey }, 'created_at');
  const d = t.data;
  const cols = [
    { key: 'created_at', label: 'Imported At', sortable: false, render: (v) => new Date(v).toLocaleString('en-IN') },
    { key: 'filename', label: 'File', sortable: false, render: (v) => <b>{v}</b> },
    { key: 'months', label: 'Months', sortable: false, render: (v) => (v || []).map(monthLabel).join(', ') || '—' },
    { key: 'processed', label: 'Records', sortable: false, render: (v, r) => (
      <span className="text-xs">{v} processed · <span className="text-emerald-700">{r.accepted} ok</span> · <span className="text-red-600">{r.rejected} rejected</span> · <span className="text-amber-700">{r.duplicates} dup</span></span>) },
    { key: 'imported_by', label: 'Imported By', sortable: false },
    { key: 'status', label: 'Status', sortable: false, render: (v, r) => (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS[v] || ''}`} title={r.mode === 'replace' ? 'Replaced earlier data' : ''}>
        {v.replace(/_/g, ' ')}{r.mode === 'replace' ? ' (replace)' : ''}</span>) },
    { key: 'reconciliation', label: 'Totals Check', sortable: false, render: (v) => (v ? (Object.values(v).every((x) => x.ok) ? <span className="text-emerald-700">Reconciled</span> : <span className="text-red-600">Mismatch</span>) : '—') },
    { key: 'actions', label: 'Actions', sortable: false, render: (_, r) => (
      <div className="flex gap-1">
        {r.error_count > 0 && <Button size="sm" variant="outline" onClick={() => downloadErrors(r)} data-testid={`import-errors-btn-${r.id}`}><Download className="w-3.5 h-3.5 mr-1" />Errors ({r.error_count})</Button>}
        {isAdmin && r.status !== 'failed' && <Button size="sm" variant="ghost" title="Re-import / replace" onClick={openImport} data-testid={`import-reimport-btn-${r.id}`}><RefreshCw className="w-3.5 h-3.5" /></Button>}
      </div>) },
  ];
  return (
    <Section title="Import History" testid="import-history-tab">
      <PosTable columns={cols} rows={d?.items} page={t.page} total={d?.total} pageSize={25} onPage={t.setPage} loading={t.loading} testid="imports-table" empty="No imports yet" />
      <p className="text-[11px] text-slate-500 mt-2">Re-import uploads a corrected file; you will see the impact on existing months and must confirm before anything is replaced.</p>
    </Section>
  );
};
